import axios from "axios";
import { store } from "../store";
import ApiService from "./api-service";
import config from "../config/index";
import ApiError from "../util/ApiError";

const userFragment = `
user {
  _id
  emails {
    address
    verified
  }
  permissions {
    global
    organisation {
      roles
      orgShortCode
    }
  }
  profile {
    firstName
    lastName
    avatarUrl
    features {
      intuitAuthMerged
      isPartnerHubUser
      receivablesForecastTrialEndDate
    }
  }
  overlays {
    collector
  }
  organisations {
    Name
    name
    ShortCode
    OrganisationID
  }
  latestAccess {
    organisation
  }
  createdAt
  hasChargebeeCustomer
  hasTOTP
  needsTOTP
}`;

const loginIntuitCallbackMutation = (qs, password) => ({
  query: `mutation loginIntuitCallback ($qs: String!, $password: String!){
    loginIntuitCallback(qs: $qs, password: $password) {
        ${userFragment}
        token
        isExternalAuthentication
        organisation {
          ShortCode
          hasCompletedSetup
        }
        isDisconnected
      }
    }`,
  variables: {
    qs,
    password,
  },
});

const loginWithTokenMutation = (token) => ({
  query: `mutation loginWithToken ($token: String!){
    loginWithToken(token: $token) {
        ${userFragment}
        token
      }
    }`,
  variables: {
    token,
  },
});

const loginXeroCallbackMutation = (qs) => ({
  query: `mutation loginXeroCallback($qs: String!){
    loginXeroCallback(qs: $qs) {
        ${userFragment}
        token
        isExternalAuthentication
        newOrganisations {
          ShortCode
        }
        existingOrganisations {
          ShortCode
        }
      }
    }`,
  variables: {
    qs,
  },
});

const loginSage200CallbackMutation = (qs) => ({
  query: `mutation loginSage200Callback($qs: String!){
    loginSage200Callback(qs: $qs) {
        ${userFragment}
        token
        state
        organisationsToConnect {
          company_id
          company_name
          site_id
          site_name
        }
        newOrganisations {
          ShortCode
        }
        existingOrganisations {
          ShortCode
        }
      }
    }`,
  variables: {
    qs,
  },
});

const loginGoogleCallbackMutation = (qs, password) => ({
  query: `mutation loginGoogleCallback($qs: String!, $password: String){
    loginGoogleCallback(qs: $qs, password: $password) {
        ${userFragment}
        token
        isExternalAuthentication
      }
    }`,
  variables: {
    qs,
    ...(password ? { password } : {}),
  },
});

const loginMicrosoftCallbackMutation = (qs, password) => ({
  query: `mutation loginMicrosoftCallback($qs: String!, $password: String){
    loginMicrosoftCallback(qs: $qs, password: $password) {
        ${userFragment}
        token
        isExternalAuthentication
      }
    }`,
  variables: {
    qs,
    ...(password ? { password } : {}),
  },
});

const recoverPasswordMutation = (email) => ({
  query: `mutation recoverPassword ($email: String!){
      recoverPassword(email: $email)
    }`,
  variables: {
    email,
  },
});

const registerMutation = ({
  firstName,
  lastName,
  email,
  platform = "web",
  bundleId,
  isInvited,
}) => ({
  query: `mutation register(
    $firstName: String!
    $lastName: String!
    $email: String!
    $platform: String
    $bundleId: String
    $isInvited: String
  ) {
    register(
      firstName: $firstName
      lastName: $lastName
      email: $email
      platform: $platform
      bundleId: $bundleId
      isInvited: $isInvited
    ) {
      token
      _id
      emails {
        address
        verified
      }
      profile {
        firstName
        lastName
      }
    }
  }`,
  variables: {
    firstName,
    lastName,
    email,
    platform,
    bundleId,
    isInvited,
  },
});

const AuthenticationService = {
  async login(email, password) {
    const body = {
      query: `mutation loginUser ($email: String!, $password: String!){
        login(email: $email, password: $password) {
          ${userFragment}
          token
          totp
        }
      }`,
      variables: {
        email,
        password,
      },
    };

    const { data: res } = await axios
      .create({
        baseURL: config.apiUrl,
        headers: {
          "Content-Type": "application/json",
        },
      })
      .post("/", body);
    const { data, errors } = res;
    if (errors && errors.length) {
      throw new ApiError(errors[0].message);
    }

    return data.login;
  },
  async loginTotp(code, jwt, view, remember) {
    let body;
    let key;
    if (view === "totp") {
      key = "loginTotp";
      body = {
        query: `mutation loginUserTotp ($code: String!, $remember: Boolean){
          loginTotp(totpToken: $code, rememberMe: $remember) {
            ${userFragment}
            token
            totp
          }
        }`,
        variables: {
          code,
          remember,
        },
      };
    } else {
      key = "loginOtp";
      body = {
        query: `mutation loginUserTotp ($code: String!, $remember: Boolean){
          loginOtp(otpToken: $code, rememberMe: $remember) {
            ${userFragment}
            totp
            token
          }
        }`,
        variables: {
          code,
          remember,
        },
      };
    }

    const { data: res } = await axios
      .create({
        baseURL: config.apiUrl,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      })
      .post("/", body);
    const { data, errors } = res;
    if (errors && errors.length) {
      throw errors;
    }
    if (!data[key].user) {
      throw new Error("Incorrect code");
    }
    return data[key];
  },
  async totpSendEmail(jwt) {
    const body = {
      query: `mutation {
        loginRequestOtp
      }`,
    };

    const { data: res } = await axios
      .create({
        baseURL: config.apiUrl,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      })
      .post("/", body);
    const { data, errors } = res;
    if (errors && errors.length) {
      throw errors;
    }
    if (!data.loginRequestOtp) {
      throw new Error("Something went wrong");
    }
    return true;
  },
  async skipTotpSetup(jwt) {
    const body = {
      query: `mutation {
        skipTOTPConfiguration
      }`,
    };

    if (jwt) {
      await ApiService.meEndpoint().post("/", body, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });
    } else {
      await ApiService.meEndpoint().post("/", body);
    }

    return true;
  },
  async setupTotp(jwt) {
    const body = {
      query: `mutation {
        setupTotp {
          key
          uri
        }
      }`,
    };

    let res;
    if (jwt) {
      res = await ApiService.meEndpoint().post("/", body, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });
    } else {
      res = await ApiService.meEndpoint().post("/", body);
    }

    return res.setupTotp;
  },
  async validateTotpSetup(code, jwt) {
    const body = {
      query: `mutation($code: String!) {
        validateTotp(token: $code)
    }`,
      variables: { code },
    };

    let res;
    if (jwt) {
      res = await ApiService.meEndpoint().post("/", body, {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      });
    } else {
      res = await ApiService.meEndpoint().post("/", body);
    }

    if (!res.validateTotp) throw new ApiError("Something went wrong");
    return true;
  },
  async requestRemoveEmail() {
    const body = {
      query: `mutation {
        requestDisableOTP
      }`,
    };

    const data = await ApiService.meEndpoint().post("/", body);
    return data.requestDisableOTP;
  },
  async removeTotp(code, view) {
    let body;
    let key;
    if (view === "totp") {
      key = "disableTOTP";
      body = {
        query: `mutation($code: String!) {
          disableTOTP(token: $code) {
            token
            totp
          }
        }`,
        variables: {
          code,
        },
      };
    } else {
      key = "disableTOTPWithOTP";
      body = {
        query: `mutation($code: String!) {
          disableTOTPWithOTP(otpToken: $code) {
            token
            totp
          }
        }
        `,
        variables: {
          code,
        },
      };
    }
    try {
      const data = await ApiService.meEndpoint().post("/", body);
      if (!data[key]) throw new Error("");
      return data[key];
    } catch (e) {
      throw new ApiError(
        view === "totp"
          ? "Invalid code"
          : "Invalid code or code may have expired",
      );
    }
  },
  async logout() {
    const state = store.getState();
    const { token } = state.loginReducer;

    if (!token) {
      return true;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const requestLogout = () =>
      axios
        .create({
          baseURL: `${config.apiUrl}/me`,
          headers,
          timeout: 2000,
        })
        .post("/", { query: "mutation { logout }" });

    try {
      const { data } = await requestLogout();
      return data?.data?.logout;
    } catch (e) {
      try {
        const { data } = await requestLogout();
        return data?.data?.logout;
      } catch {
        return true;
      }
    }
  },
  async loginIntuit() {
    const data = await ApiService.baseEndpoint().post("/", {
      query: `mutation {
         loginIntuit {
           url
          }
        }`,
    });
    return data.loginIntuit;
  },
  async loginIntuitCallback(qs, password = "") {
    const { loginIntuitCallback } = await ApiService.baseEndpoint().post("/", {
      ...loginIntuitCallbackMutation(qs, password),
    });
    return { data: { loginIntuitCallback } };
  },
  async loginXero() {
    const data = await ApiService.baseEndpoint().post("/", {
      query: `mutation{ loginXero { url } }`,
    });
    return data.loginXero;
  },
  async loginXeroCallback(qs) {
    const { loginXeroCallback } = await ApiService.baseEndpoint().post("/", {
      ...loginXeroCallbackMutation(qs),
    });
    return { data: { loginXeroCallback } };
  },
  async loginGoogle() {
    const data = await ApiService.baseEndpoint().post("/", {
      query: `mutation{ loginGoogle { url } }`,
    });
    return data.loginGoogle;
  },
  async loginGoogleCallback(qs, password) {
    const { loginGoogleCallback } = await ApiService.baseEndpoint().post("/", {
      ...loginGoogleCallbackMutation(qs, password),
    });
    return { data: { loginGoogleCallback } };
  },
  async loginMicrosoft() {
    const data = await ApiService.baseEndpoint().post("/", {
      query: `mutation{ loginMicrosoft { url } }`,
    });
    return data.loginMicrosoft;
  },
  async loginMicrosoftCallback(qs, password) {
    const { loginMicrosoftCallback } = await ApiService.baseEndpoint().post(
      "/",
      {
        ...loginMicrosoftCallbackMutation(qs, password),
      },
    );
    return { data: { loginMicrosoftCallback } };
  },
  async loginSage200Callback(qs) {
    const { loginSage200Callback } = await ApiService.baseEndpoint().post("/", {
      ...loginSage200CallbackMutation(qs),
    });
    return { data: { loginSage200Callback } };
  },
  async loginWithToken(token) {
    const { loginWithToken } = await ApiService.baseEndpoint().post(
      "/",
      loginWithTokenMutation(token),
    );
    if (!loginWithToken) throw new Error("Failed to login");
    return loginWithToken;
  },
  async recoverPassword(email) {
    const { data } = await axios
      .create({
        baseURL: config.apiUrl,
        headers: {
          "Content-Type": "application/json",
        },
      })
      .post("/", { ...recoverPasswordMutation(email) });
    return data;
  },
  async register({
    firstName,
    lastName,
    email,
    platform = "web",
    bundleId,
    isInvited,
  }) {
    const { data: res } = await axios
      .create({
        baseURL: config.apiUrl,
        headers: {
          "Content-Type": "application/json",
        },
      })
      .post(
        "/",
        registerMutation({
          firstName,
          lastName,
          email,
          platform,
          bundleId,
          isInvited,
        }),
      );

    const { data, errors } = res;
    if (errors && errors.length) {
      throw new ApiError(errors[0].message);
    }

    return data.register;
  },
  async setNewPassword(email, token, password) {
    const body = {
      query: `mutation setNewPassword ($email: String!, $token: String!, $password: String!){
          setNewPassword(email: $email, token: $token, password: $password) {
            ${userFragment}
            token
            totp
          }
        }`,
      variables: {
        email,
        token,
        password,
      },
    };

    const data = await ApiService.baseEndpoint().post("/", body);

    return data.setNewPassword;
  },
  async updateExistingPassword({ currentPassword, newPassword }) {
    const body = {
      query: `mutation updateExistingPassword ($currentPassword: String!, $newPassword: String!){
        updateExistingPassword(currentPassword: $currentPassword, newPassword: $newPassword) {
          ${userFragment}
          token
          totp
        }
      }`,
      variables: {
        currentPassword,
        newPassword,
      },
    };

    const data = await ApiService.meEndpoint().post("/", body);
    return data.updateExistingPassword;
  },
  async setInvitedUserRole({ roles, email }, shortCode) {
    const body = {
      query: `mutation setInvitedUserRole($roles: [String]!, $email: String!){
        setInvitedUserRole(roles: $roles, email: $email) {
          invitedChaserUsers {
            email
            roles
          }
        }
      }`,
      variables: {
        roles,
        email,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.setInvitedUserRole;
  },
  async setRolePermission({ roles, userId }, shortCode) {
    const body = {
      query: `mutation setRolePermission($roles: [String!], $userId: String!){
        setRolePermission(roles: $roles, userId: $userId)
      }`,
      variables: {
        userId,
        roles,
      },
    };
    const data = await ApiService.orgEndpoint(shortCode).post("/", body);
    return data.setRolePermission;
  },
};

export default AuthenticationService;
